import React, { useCallback, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import CloseIcon from "@mui/icons-material/Close";
import EventIcon from "@mui/icons-material/Event";
import PersonIcon from "@mui/icons-material/Person";
import LocalPhoneIcon from "@mui/icons-material/LocalPhone";
import MedicalInformationIcon from "@mui/icons-material/MedicalInformation";
import SmsOutlinedIcon from "@mui/icons-material/SmsOutlined";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import MeetingRoomIcon from "@mui/icons-material/MeetingRoom";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import baseUrl from "../../../../api/baseUrl";
import socket from "../../../../api/socket";
import {
  APPOINTMENT_STATUS_OPTIONS,
  bookingSourceStyles,
  doctorNameFromItem,
  FIELD_LABELS,
  formatAppointmentPurpose,
  formatPatientNameWithTitle,
  formatPreAppointmentStatus,
  formatQueueFromItem,
  isAppointmentCancelled,
  isRoomAllocationEnabled,
  normalizeLifecycleStatus,
  parseQueueInput,
  preAppointmentStatusFromItem,
  purposeIdsFromAppointment,
  statusStyles,
  transferDoctorNamesFromItem,
} from "./appointmentViewShared";
import { isDoctorTransferEnabled } from "../../../../utils/clinicFeatures";
import { appointmentPatientId, openPatientSummaryFromAppointment } from "../../../../utils/openPatientSummaryInNewTab";
import { appointmentHasToken } from "../../../../utils/dispatchTokenPrint";
import { useOngoingStatusGate } from "./useOngoingStatusGate";
import { notifySmsBalanceChanged } from "../../../../utils/sms/smsToast";

type AppointmentDetailsModalProps = {
  item: Record<string, any>;
  open: boolean;
  onClose: () => void;
  onStatusChange?: () => void;
  onOngoingStatus?: (patientId: string) => void;
};

function ymdFromAppointmentDate(date: unknown): string {
  if (date == null) return "";
  const raw = String(date);
  if (raw.includes("T")) return raw.split("T")[0];
  if (/^\d{4}-\d{2}-\d{2}/.test(raw)) return raw.slice(0, 10);
  return "";
}

function hmFromStoredTime(time?: string | null): string {
  const m = String(time ?? "").trim().match(/^(\d{1,2}):(\d{2})/);
  if (!m) return "";
  return `${m[1].padStart(2, "0")}:${m[2]}`;
}

function storedFromHm(hm: string): string {
  const t = String(hm ?? "").trim();
  if (/^\d{2}:\d{2}:\d{2}$/.test(t)) return t;
  if (/^\d{2}:\d{2}$/.test(t)) return `${t}:00`;
  return t;
}

function minutesBetweenHm(startHm: string, endHm: string): number {
  const [sh, sm] = startHm.split(":").map(Number);
  const [eh, em] = endHm.split(":").map(Number);
  if (![sh, sm, eh, em].every(Number.isFinite)) return 0;
  return eh * 60 + em - (sh * 60 + sm);
}

function doctorIdFromItem(item: Record<string, any>): string {
  const d = item?.doctorId;
  if (d && typeof d === "object") return String(d._id ?? d.id ?? "");
  return d != null ? String(d) : "";
}

function InfoChip({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode;
  label: string;
  value: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-900/50 p-3">
      <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">
        {icon}
        {label}
      </div>
      <div className="text-sm font-semibold text-slate-800 dark:text-slate-100 truncate">
        {value}
      </div>
    </div>
  );
}

export default function AppointmentDetailsModal({
  item,
  open,
  onClose,
  onStatusChange,
  onOngoingStatus,
}: AppointmentDetailsModalProps) {
  const roomAllocationEnabled = isRoomAllocationEnabled();
  const doctorTransferEnabled = isDoctorTransferEnabled();
  const navigate = useNavigate();
  const [status, setStatus] = useState(
    () => normalizeLifecycleStatus(item?.status) || item?.status || ""
  );
  const [queueInput, setQueueInput] = useState("");
  const [editDate, setEditDate] = useState("");
  const [editStart, setEditStart] = useState("");
  const [editEnd, setEditEnd] = useState("");
  const [timeSaving, setTimeSaving] = useState(false);
  const [statusSaving, setStatusSaving] = useState(false);
  const [queueSaving, setQueueSaving] = useState(false);
  const [treatmentLoading, setTreatmentLoading] = useState(false);
  const [smsSending, setSmsSending] = useState(false);
  const { gateStatus, ongoingGateModal } = useOngoingStatusGate();

  useEffect(() => {
    if (!open) return;
    setStatus(normalizeLifecycleStatus(item?.status) || item?.status || "");
    setQueueInput(formatQueueFromItem(item));
    setEditDate(ymdFromAppointmentDate(item?.date));
    setEditStart(hmFromStoredTime(item?.startTime));
    setEditEnd(hmFromStoredTime(item?.endTime));
  }, [open, item?.status, item?.queue, item?.date, item?.startTime, item?.endTime]);

  const patientName =
    formatPatientNameWithTitle(item?.patientId) ||
    formatPatientNameWithTitle(item?.bookPatientId) ||
    item?.patientId?.fullName ||
    item?.bookPatientId?.fullName ||
    "N/A";
  const mobile =
    item?.patientId?.mobileNo ||
    item?.bookPatientId?.mobileNo ||
    item?.mobileNo ||
    "";
  const doctorName = doctorNameFromItem(item);
  const transferDoctorNames = transferDoctorNamesFromItem(item);
  const purpose = formatAppointmentPurpose(item);
  const patientId = appointmentPatientId(item);
  const hasPatient = Boolean(item?.patientId?.fullName && patientId);
  const canEditTime = !appointmentHasToken(item?.queue);

  const emitAppointmentSync = useCallback(
    (statusVal: string, queueNum?: number) => {
      if (!item?._id) return;
      const payload: Record<string, unknown> = {
        appointmentid: item._id,
        status: statusVal,
        roomNumber: item?.roomNumber,
        sessionName: item?.sessionName,
      };
      if (roomAllocationEnabled && queueNum != null) {
        payload.queue = queueNum;
      } else if (item?.queue != null && item?.queue !== "") {
        payload.queue = item.queue;
      }
      socket.emit("allAppointmentStatus", payload);
      if (
        roomAllocationEnabled &&
        statusVal === "ongoing" &&
        queueNum != null
      ) {
        try {
          localStorage.setItem(
            "nextQueue",
            String(Math.max(0, Math.floor(queueNum)))
          );
        } catch {
          /* ignore */
        }
      }
    },
    [item, roomAllocationEnabled]
  );

  const handleStatusChange = useCallback(
    async (e: React.ChangeEvent<HTMLSelectElement>) => {
      const next = e.target.value;
      if (!next || !item?._id) return;

      const ok = await gateStatus(item, next);
      if (!ok) {
        e.target.value = status;
        return;
      }

      const queueNum = roomAllocationEnabled
        ? parseQueueInput(queueInput) ?? 0
        : undefined;

      setStatus(next);
      setStatusSaving(true);

      emitAppointmentSync(next, queueNum);

      if (next === "ongoing") {
        openPatientSummaryFromAppointment(
          item,
          roomAllocationEnabled ? queueNum : undefined
        );
      }

      baseUrl
        .patch(
          "/appointment/statusAppointmentById",
          { status: next },
          { headers: { appointmentid: item._id, status: next } }
        )
        .then(() => onStatusChange?.())
        .catch((err) => {
          console.error(err);
          setStatus(normalizeLifecycleStatus(item?.status) || item?.status || "");
          toast.error(
            err?.response?.data?.error || "Could not update appointment status."
          );
        })
        .finally(() => setStatusSaving(false));
    },
    [
      item,
      onStatusChange,
      queueInput,
      emitAppointmentSync,
      roomAllocationEnabled,
      gateStatus,
      status,
    ]
  );

  const handleQueueSave = useCallback(() => {
    if (!item?._id) return;
    const parsed = parseQueueInput(queueInput);
    if (parsed === null) return;

    setQueueSaving(true);
    baseUrl
      .patch(
        "/appointment/queueAppointmentById",
        { queue: parsed },
        { headers: { appointmentid: item._id } }
      )
      .then(() => {
        setQueueInput(String(parsed));
        emitAppointmentSync(status || item?.status || "appointment", parsed);
        onStatusChange?.();
      })
      .catch((err) => console.error(err))
      .finally(() => setQueueSaving(false));
  }, [item, queueInput, status, emitAppointmentSync, onStatusChange]);

  const handleTimeSave = useCallback(() => {
    if (!item?._id || !canEditTime) return;
    if (!editDate || !editStart || !editEnd) {
      toast.error("Date, start time, and end time are required");
      return;
    }
    const intervalMinutes = minutesBetweenHm(editStart, editEnd);
    if (intervalMinutes <= 0) {
      toast.error("End time must be after start time");
      return;
    }
    const doctorId = doctorIdFromItem(item);
    const pid = appointmentPatientId(item);
    if (!doctorId || !pid) {
      toast.error("Doctor and patient are required to change the time");
      return;
    }

    setTimeSaving(true);
    const startTime = storedFromHm(editStart);
    const endTime = storedFromHm(editEnd);
    baseUrl
      .patch(
        "/appointment/reschedule",
        {
          doctorId,
          patientId: pid,
          date: editDate,
          startTime,
          endTime,
          interval: String(intervalMinutes),
          purpose: purposeIdsFromAppointment(item),
        },
        {
          headers: {
            appointmentid: item._id,
            status: status || item?.status || "appointment",
          },
        }
      )
      .then(() => {
        toast.success("Visit time updated");
        emitAppointmentSync(status || item?.status || "appointment");
        onStatusChange?.();
      })
      .catch((err) => {
        toast.error(
          err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Could not update time"
        );
      })
      .finally(() => setTimeSaving(false));
  }, [
    item,
    canEditTime,
    editDate,
    editStart,
    editEnd,
    status,
    emitAppointmentSync,
    onStatusChange,
  ]);

  const queueDisplay = (() => {
    const p = parseQueueInput(queueInput);
    if (p === null) return "—";
    return String(p);
  })();

  const handleAddTreatment = useCallback(() => {
    if (!patientId) return;
    setTreatmentLoading(true);
    const appointmentId = item?._id ? String(item._id) : undefined;
    baseUrl
      .post("treatment/create-treatment", {
        patientId,
        ...(appointmentId ? { appointmentId } : {}),
      })
      .then((response) => {
        onClose();
        const qs = appointmentId
          ? `?appointmentId=${encodeURIComponent(appointmentId)}`
          : "";
        navigate(
          `/client/treatment-diagnose/add-treatment/treatment-table/${response.data.data._id}${qs}`
        );
      })
      .catch((err) => console.error(err))
      .finally(() => setTreatmentLoading(false));
  }, [patientId, navigate, onClose, item?._id]);

  const handleConfirmSms = useCallback(() => {
    if (!item?._id) return;
    if (!mobile) {
      toast.error("Patient has no phone number");
      return;
    }
    if (!hasPatient) {
      toast.error("No patient linked to this appointment");
      return;
    }

    setSmsSending(true);
    baseUrl
      .post(
        "/appointment/sendAppointmentConfirmSms",
        {},
        { headers: { appointmentid: item._id } }
      )
      .then((response) => {
        const sms = response.data?.sms;
        if (sms?.sent || response.data?.success) {
          toast.success(response.data?.message || "Confirmation SMS sent");
          notifySmsBalanceChanged();
        } else {
          toast.error(
            response.data?.message ||
              response.data?.error ||
              "Failed to send confirmation SMS"
          );
        }
      })
      .catch((err) => {
        const msg =
          err?.response?.data?.error ||
          err?.response?.data?.message ||
          "Failed to send confirmation SMS";
        toast.error(msg);
      })
      .finally(() => setSmsSending(false));
  }, [item?._id, mobile, hasPatient]);

  if (!open) {
    return <>{ongoingGateModal}</>;
  }

  return (
    <>
    {createPortal(
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="appointment-details-title"
    >
      <button
        type="button"
        className="absolute inset-0 bg-black/50 backdrop-blur-[2px]"
        onClick={onClose}
        aria-label="Close dialog"
      />
      <div className="relative w-full sm:max-w-lg max-h-[92vh] sm:max-h-[90vh] overflow-hidden rounded-t-2xl sm:rounded-2xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-600 flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-primary to-primaryLight px-5 py-4 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center shrink-0">
              <EventIcon className="text-white" />
            </div>
            <div className="min-w-0">
              <h2
                id="appointment-details-title"
                className="text-lg font-bold text-white truncate"
              >
                Appointment
              </h2>
              <p className="text-xs text-white/80 truncate">
                {item?.date?.split("T")[0] ?? "—"}
                {item?.startTime && item?.endTime
                  ? ` · ${item.startTime}–${item.endTime}`
                  : ""}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-9 h-9 bg-white/20 rounded-xl flex items-center justify-center hover:bg-white/30 transition-colors shrink-0"
            aria-label="Close"
          >
            <CloseIcon className="text-white" sx={{ fontSize: 20 }} />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-5 space-y-4">
          {/* Patient card */}
          <div className="rounded-2xl bg-gradient-to-br from-primary/10 to-primary/5 dark:from-primary/20 dark:to-slate-800 border border-primary/20 p-4">
            <div className="flex items-start gap-3">
              <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary to-primaryLight flex items-center justify-center text-white font-bold text-lg shrink-0 shadow-md">
                {patientName.charAt(0).toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <PersonIcon
                    className="text-primary shrink-0"
                    sx={{ fontSize: 18 }}
                  />
                  <span className="font-semibold text-slate-900 dark:text-slate-100 truncate">
                    {patientName}
                  </span>
                </div>
                {mobile ? (
                  <a
                    href={`tel:${mobile.replace(/[\s\-()]/g, "")}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-sm text-slate-600 dark:text-slate-300 hover:text-primary transition-colors"
                    onClick={(e) => {
                      if (window.innerWidth >= 768) e.preventDefault();
                    }}
                  >
                    <LocalPhoneIcon sx={{ fontSize: 15 }} />
                    {mobile}
                  </a>
                ) : null}
                <div className="mt-2 flex flex-wrap gap-2">
                  {(() => {
                    const source = preAppointmentStatusFromItem(item);
                    const label = formatPreAppointmentStatus(source);
                    if (!label) return null;
                    return (
                      <span
                        className={`px-2 py-0.5 rounded-md text-xs font-semibold border ${
                          bookingSourceStyles[source] ||
                          bookingSourceStyles.default
                        }`}
                      >
                        {source === "cancel"
                          ? "Cancelled"
                          : `Booked · ${label}`}
                      </span>
                    );
                  })()}
                  {roomAllocationEnabled ? (
                    <span className="px-2 py-0.5 rounded-md text-xs font-medium bg-slate-200/80 dark:bg-slate-700 text-slate-700 dark:text-slate-200 tabular-nums">
                      Token #{queueDisplay}
                    </span>
                  ) : null}
                </div>
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="space-y-2">
            <label
              htmlFor="appointment-modal-status"
              className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide"
            >
              Status
            </label>
            {isAppointmentCancelled(item) ? (
              <p className="px-4 py-2.5 text-sm text-slate-400 dark:text-slate-500">
                —
              </p>
            ) : (
              <select
                id="appointment-modal-status"
                value={normalizeLifecycleStatus(status) || status || ""}
                onChange={handleStatusChange}
                disabled={statusSaving || !item?._id}
                className={`w-full px-4 py-2.5 rounded-xl text-sm font-medium border cursor-pointer focus:outline-none focus:ring-2 focus:ring-primary/25 transition-colors disabled:opacity-60 ${
                  statusStyles[normalizeLifecycleStatus(status) || status] ||
                  statusStyles.default
                }`}
              >
                <option value="">Select status</option>
                {APPOINTMENT_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
                <option value="cancel">Cancel</option>
              </select>
            )}
            {statusSaving ? (
              <p className="text-xs text-slate-500">Updating status…</p>
            ) : null}
          </div>

          {roomAllocationEnabled ? (
            <div className="space-y-2">
              <label
                htmlFor="appointment-modal-queue"
                className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide"
              >
                {FIELD_LABELS.queue}
              </label>
              <div className="flex gap-2">
                <input
                  id="appointment-modal-queue"
                  type="number"
                  min={0}
                  step={1}
                  value={queueInput}
                  onChange={(e) => {
                    const v = e.target.value;
                    if (v === "" || /^\d+$/.test(v)) setQueueInput(v);
                  }}
                  disabled={queueSaving || !item?._id}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm font-medium border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-primary/25 tabular-nums"
                  placeholder="0"
                />
                <button
                  type="button"
                  onClick={handleQueueSave}
                  disabled={queueSaving || !item?._id}
                  className="px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-40 transition-opacity shrink-0"
                >
                  {queueSaving ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          ) : null}

          {/* Quick info grid */}
          {canEditTime ? (
            <div className="rounded-xl border border-slate-200 dark:border-slate-600 bg-slate-50/80 dark:bg-slate-900/40 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide">
                <AccessTimeIcon sx={{ fontSize: 14 }} />
                Date & time
              </div>
              <p className="text-xs text-slate-500">
                You can change the time until a token is generated.
              </p>
              <input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                disabled={timeSaving || !item?._id}
                className="w-full px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-primary/25"
              />
              <div className="flex items-center gap-2">
                <input
                  type="time"
                  value={editStart}
                  onChange={(e) => setEditStart(e.target.value)}
                  disabled={timeSaving || !item?._id}
                  className="flex-1 px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-primary/25"
                />
                <span className="text-xs text-slate-400">–</span>
                <input
                  type="time"
                  value={editEnd}
                  onChange={(e) => setEditEnd(e.target.value)}
                  disabled={timeSaving || !item?._id}
                  className="flex-1 px-3 py-2 rounded-xl text-sm font-medium border border-slate-200 dark:border-slate-600 bg-white dark:bg-slate-800 text-slate-800 dark:text-slate-100 outline-none focus:ring-2 focus:ring-primary/25"
                />
              </div>
              <button
                type="button"
                onClick={handleTimeSave}
                disabled={timeSaving || !item?._id}
                className="w-full px-4 py-2.5 rounded-xl text-sm font-semibold bg-primary text-white hover:opacity-90 disabled:opacity-40 transition-opacity"
              >
                {timeSaving ? "Saving…" : "Save time"}
              </button>
            </div>
          ) : null}
          <div className="grid grid-cols-2 gap-2">
            {!canEditTime ? (
              <>
                <InfoChip
                  icon={<CalendarTodayIcon sx={{ fontSize: 14 }} />}
                  label="Date"
                  value={item?.date?.split("T")[0] ?? "—"}
                />
                <InfoChip
                  icon={<AccessTimeIcon sx={{ fontSize: 14 }} />}
                  label="Time"
                  value={
                    item?.startTime && item?.endTime
                      ? `${item.startTime} – ${item.endTime}`
                      : "—"
                  }
                />
              </>
            ) : null}
            <InfoChip
              icon={<MeetingRoomIcon sx={{ fontSize: 14 }} />}
              label="Room"
              value={String(item?.roomNumber ?? "").trim() || "—"}
            />
            {roomAllocationEnabled ? (
              <InfoChip
                icon={<FormatListNumberedIcon sx={{ fontSize: 14 }} />}
                label="Token"
                value={queueDisplay}
              />
            ) : null}
          </div>

          {/* Details list */}
          <div className="rounded-xl border border-slate-200 dark:border-slate-600 divide-y divide-slate-100 dark:divide-slate-700">
            <div className="px-4 py-3 flex justify-between gap-3 text-sm">
              <span className="text-slate-500 dark:text-slate-400">Doctor</span>
              <span className="font-medium text-slate-800 dark:text-slate-100 text-right">
                {doctorName}
              </span>
            </div>
            {doctorTransferEnabled && transferDoctorNames ? (
              <div className="px-4 py-3 flex justify-between gap-3 text-sm">
                <span className="text-slate-500 dark:text-slate-400">
                  Transferred to
                </span>
                <span className="font-medium text-sky-700 dark:text-sky-300 text-right">
                  {transferDoctorNames}
                </span>
              </div>
            ) : null}
            <div className="px-4 py-3 flex justify-between gap-3 text-sm">
              <span className="text-slate-500 dark:text-slate-400">Session</span>
              <span className="font-medium text-slate-800 dark:text-slate-100 text-right">
                {item?.sessionName || "—"}
              </span>
            </div>
            <div className="px-4 py-3 flex justify-between gap-3 text-sm">
              <span className="text-slate-500 dark:text-slate-400">Purpose</span>
              <span className="font-medium text-slate-800 dark:text-slate-100 text-right">
                {purpose}
              </span>
            </div>
          </div>
        </div>

        {/* Actions */}
        <div className="shrink-0 px-5 py-4 border-t border-slate-200 dark:border-slate-600 bg-slate-50/90 dark:bg-slate-900/50 flex flex-wrap gap-2">
          {hasPatient && mobile ? (
            <button
              type="button"
              onClick={handleConfirmSms}
              disabled={smsSending || !item?._id}
              className="flex-1 min-w-[7.5rem] flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-sky-600 text-white hover:bg-sky-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              <SmsOutlinedIcon sx={{ fontSize: 18 }} />
              {smsSending ? "Sending…" : "Confirm SMS"}
            </button>
          ) : null}
          {hasPatient ? (
            <button
              type="button"
              onClick={handleAddTreatment}
              disabled={treatmentLoading}
              className="flex-1 min-w-[7.5rem] flex items-center justify-center gap-2 px-3 py-2.5 rounded-xl text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-40 disabled:pointer-events-none transition-colors"
            >
              <MedicalInformationIcon sx={{ fontSize: 18 }} />
              {treatmentLoading ? "Loading…" : "Add treatment"}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className={`px-4 py-2.5 rounded-xl text-sm font-semibold bg-slate-200/80 dark:bg-slate-700 text-slate-700 dark:text-slate-200 hover:bg-slate-300 dark:hover:bg-slate-600 transition-colors ${
              hasPatient || mobile ? "flex-1 min-w-[5rem]" : "w-full"
            }`}
          >
            Close
          </button>
        </div>
      </div>
    </div>,
    document.body
    )}
    {ongoingGateModal}
    </>
  );
}
